import { getAllAxisGroupNodes } from './axisNodes'

const PAD = 10
const MIN_OVERFLOW = 4
const MAX_LEFT_EXPANSION = 160

// Converts a client-space rectangle corner to SVG viewport coordinates.
// svgNode.getScreenCTM() maps SVG user units → screen pixels, so its inverse
// maps screen pixels → SVG user units.
function clientToSvg(svgNode, invCtm, clientX, clientY) {
  const pt = svgNode.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  return pt.matrixTransform(invCtm)
}

// Measures the SVG-space bounding extent of all .tick text nodes in the
// given axis groups.
//
// Uses getBoundingClientRect() — the only unambiguous measurement that
// correctly captures visually-rotated text (getBBox/getCTM have
// coordinate-space pitfalls when the text itself carries a rotation transform).
function measureTickTextExtents(axisGroups, svgNode) {
  let invCtm
  try {
    const ctm = svgNode.getScreenCTM()
    if (!ctm) return null
    invCtm = ctm.inverse()
  } catch { return null }

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  let found = false

  for (const group of axisGroups) {
    const ticks = Array.from(group.querySelectorAll('.tick'))
    for (const tick of ticks) {
      const textNode = tick.querySelector('text')
      if (!textNode) continue
      if (textNode.style.display === 'none') continue

      let rect
      try { rect = textNode.getBoundingClientRect() } catch { continue }
      // Skip zero-size rects (hidden or unmeasured elements)
      if (!rect || (rect.width === 0 && rect.height === 0)) continue

      // Convert all four screen corners to SVG user-unit space.  Doing all
      // four ensures rotated labels (which have non-rectangular client rects
      // as seen by the containing div) are fully bounded.
      const corners = [
        clientToSvg(svgNode, invCtm, rect.left,  rect.top),
        clientToSvg(svgNode, invCtm, rect.right, rect.top),
        clientToSvg(svgNode, invCtm, rect.left,  rect.bottom),
        clientToSvg(svgNode, invCtm, rect.right, rect.bottom),
      ]
      for (const { x, y } of corners) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
      found = true
    }
  }

  return found ? { minX, maxX, minY, maxY } : null
}

/**
 * After a chart has rendered and the SVG transform pipeline has run,
 * measures whether axis tick labels overflow the background rect boundaries
 * and returns suggested margin increases.
 *
 * Only measures axis tick text — ignores legends, series labels, and
 * line-end annotations that legitimately extend outside the plot area.
 *
 * Returns null when no adjustment is needed.
 * Only ever suggests increases — never reduces a margin the user set manually.
 */
export function suggestMarginAdjustments(svgNode, visualOptions = {}) {
  if (!svgNode) return null

  const bgRect = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  if (!bgRect) return null

  const bgX = parseFloat(bgRect.getAttribute('x') || '0')
  const bgY = parseFloat(bgRect.getAttribute('y') || '0')
  const bgW = parseFloat(bgRect.dataset.lugsailBaseWidth  || bgRect.getAttribute('width')  || '0')
  const bgH = parseFloat(bgRect.dataset.lugsailBaseHeight || bgRect.getAttribute('height') || '0')
  if (!bgW || !bgH) return null

  const suggestions = {}

  // Left overflow — y-axis tick labels wider than marginLeft
  const yGroups = getAllAxisGroupNodes(svgNode, 'y')
  if (yGroups.length) {
    const ext = measureTickTextExtents(yGroups, svgNode)
    if (ext) {
      const leftOverflow = bgX - ext.minX
      if (leftOverflow > MIN_OVERFLOW && leftOverflow <= MAX_LEFT_EXPANSION) {
        const current = Number(visualOptions.marginLeft) || 0
        suggestions.marginLeft = Math.ceil(current + leftOverflow + PAD)
      }
    }
  }

  // Bottom overflow — x-axis tick labels (rotated) taller than marginBottom.
  // Compare against the base height (stamped before ensureSvgBottomPadding
  // expands the background rect) so we measure against the declared chart
  // boundary rather than the already-expanded one.
  const xGroups = getAllAxisGroupNodes(svgNode, 'x')
  if (xGroups.length) {
    const ext = measureTickTextExtents(xGroups, svgNode)
    if (ext) {
      const bgBottom = bgY + bgH
      const bottomOverflow = ext.maxY - bgBottom
      if (bottomOverflow > MIN_OVERFLOW) {
        const current = Number(visualOptions.marginBottom) || 0
        suggestions.marginBottom = Math.ceil(current + bottomOverflow + PAD)
      }
    }
  }

  return Object.keys(suggestions).length ? suggestions : null
}
